import { faCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { faPlayCircle } from "@fortawesome/pro-regular-svg-icons/faPlayCircle";
import { faUsersViewfinder } from "@fortawesome/pro-regular-svg-icons/faUsersViewfinder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CardContent, Grid, IconButton, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { CorePermissions } from "app/appPermissions";
import { AppTransComponents } from "app/appTrans";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcContainer, MprcFormBarHeader, MprcFormSubHeader, MprcIconButton, MprcIconButtonDropdown, MprcIconButtonMoreOptionsDropdown, MprcIconFa, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from "components";
import { ButtonAdd, ButtonBack } from "components/buttons";
import { MprcFilterCount, MprcFilterLine } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonBlocked } from "components/iconButtons";
import { IconSort } from "components/icons";
import { MprcCaptionBullets } from "components/MprcCaptionBullets";
import { MprcCaretSwitch } from "components/MprcCaretSwitch";
import { MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcNotice } from "components/MprcNotice";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { ManagedShortVideoListResponse, ManagedShortVideoListResponsePaginatedList, ShortVideoListOrderBy } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PreviewShortVideoDialog } from "views/knowledge/shorts/dialogs/PreviewShortVideoDialog";
import { PublicationSelectorShortVideoDrawer } from "views/knowledge/shorts/drawers/PublicationSelectorShortVideoDrawer";
import { EditShortVideoDialog } from "views/knowledge/shorts/Home/EditShorts/EditShortVideoDialog";

////////////////////////////////////////////////////////////////////////////

interface IFilterOptions {
    searchText: string;
    showExpired: boolean;
    adminList: boolean;
}

interface IViewDataFilter {
    pageNumber: number;
    orderBy: ShortVideoListOrderBy;
    filter: IFilterOptions;
}

type MenuOptions = "delete";

////////////////////////////////////////////////////////////////////////////

function getDefaultViewFilter(): IViewDataFilter {
    return {
        pageNumber: 1,
        orderBy: ShortVideoListOrderBy.Title,
        filter: {
            searchText: "",
            showExpired: false,
            adminList: false,
        }
    };
}

////////////////////////////////////////////////////////////////////////////

export const ManageShortVideos: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { permissionExplorer } = useSession();

    const [dialog, setDialog] = useState<any>();
    const [viewData, setViewData] = useState<ManagedShortVideoListResponsePaginatedList | null>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteShortVideoId, setDeleteShortVideoId] = useState<string | null>(null);
    const [filtered, setFiltered] = useState<boolean>(false);
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>(getDefaultViewFilter());
    const [showNotice, setShowNotice] = useState<boolean>(true);
    const [noAccess, setNoAccess] = useState<boolean>(false);
    const [containsConfidential, setContainsConfidential] = useState<boolean>(false);

    const searchTextField = useRef<MprcSearchTextFieldHandle | null>(null);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleAdd = () => {
        setDialog(
            <PublicationSelectorShortVideoDrawer
                multi={false}
                onClose={
                    (publications) => {
                        if (publications && publications.length == 1) {
                            handleEdit(null, publications[0].publicationId, publications[0].title);
                        }
                        else {
                            setDialog(null);
                        }
                    }
                }
            />
        );
    };

    ///
    const handleEdit = (shortVideoId: string | null, publicationId: string, title: string) => {
        setDialog(<EditShortVideoDialog
            shortVideoId={shortVideoId}
            publicationId={publicationId}
            title={title}
            allowDelete={false}
            onClose={
                (reload) => {
                    setDialog(null);
                    if (reload === true) handleReload();
                }
            }
        />);
    };

    ///
    const handleReload = useCallback(() => {
        setLoading(true);
    }, []);

    ///
    const handleFilterChange = useCallback((filter: Partial<IFilterOptions>, reload: boolean) => {
        setViewFilter(state => ({ ...state, filter: { ...state.filter, ...filter } }));
        if (reload) handleReload();
    }, []);

    ///
    const handleOrderBy = useCallback((orderBy: ShortVideoListOrderBy) => {
        setViewFilter(state => ({ ...state, orderBy }));
        handleReload();
    }, []);

    ///
    const resetFilters = () => {
        searchTextField.current?.reset();
        setViewFilter((state) => ({ ...state, filter: { searchText: "", showExpired: false, adminList: false } }));
        handleReload();
    };

    ///
    const handlePreview = (item: ManagedShortVideoListResponse) => {
        setDialog(<PreviewShortVideoDialog
            shortVideoId={item.shortVideoId}
            title={item.title}
            onClose={() => setDialog(null)}
        />);
    };

    ///
    const handleSelectUserOption = (option: MenuOptions, item: ManagedShortVideoListResponse) => {
        switch (option) {
            case "delete":
                setDialog(
                    <MprcConfirmDialog
                        open
                        severity="danger"
                        message={
                            <Trans
                                ns="knowledge"
                                i18nKey="ShortVideo.List.ConfirmDeleteMessage"
                                values={{ name: item.title }}
                                components={AppTransComponents}
                            />
                        }
                        onClose={
                            (result) => {
                                if (result) setDeleteShortVideoId(item.shortVideoId);
                                setDialog(null);
                            }
                        }
                    />
                );
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        async function deleteVideo() {
            try {
                const result = await api.v1.knowledgeShortVideo.deleteShortVideo({ shortVideoId: deleteShortVideoId! });
                if (result.data === true) {
                    enqueueSnackbar(t("ShortVideo.List.DeleteSuccessMessage"), { variant: "success" });
                    if (viewData?.items.length == 1 && viewFilter.pageNumber > 1) setViewFilter(state => ({ ...state, pageNumber: state.pageNumber - 1 }));
                    setLoading(true);
                }
                else enqueueSnackbar(t("ShortVideo.List.DeleteErrorMessage"), { variant: "error" });

            } catch {
                enqueueSnackbar(t("ShortVideo.List.DeleteErrorMessage"), { variant: "error" });
            }

            setDeleteShortVideoId(null);
        }
        if (deleteShortVideoId != null) deleteVideo();
    }, [deleteShortVideoId]);

    ///
    useEffect(() => {
        async function checkConfidential() {
            try {
                const result = await api.v1.knowledgeShortVideo.managingContainsConfidentialPublication({
                    searchText: viewFilter.filter.searchText ?? null,
                    showExpired: viewFilter.filter.showExpired ?? null,
                });
                setContainsConfidential(result.data);
            } catch {
                setContainsConfidential(false);
            }
        }
        if (viewFilter.filter.adminList) checkConfidential();
        else setContainsConfidential(false);
    }, [viewFilter.filter.adminList]);

    ///
    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.knowledgeShortVideo.managedList({
                    searchText: viewFilter.filter.searchText ?? null,
                    orderBy: viewFilter.orderBy,
                    showExpired: viewFilter.filter.showExpired ?? null,
                    isAdminList: viewFilter.filter.adminList ?? false,
                    pageNumber: viewFilter.pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT
                });
                setViewData(result.data);

                setFiltered((viewFilter.filter.searchText?.length || 0) > 0 || viewFilter.filter.showExpired === true);

            } catch (e: any) {
                setViewData(null);
                if (e?.status == 403) setNoAccess(true);
                else setViewError(e);
            }

            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);

    ///
    const getUserMenuItems = useMemo(() => {
        const group1 = [] as MprcDropdownItems<MenuOptions>;
        group1.push({ text: <MprcText color="error">{t("shared:Actions.Delete")}</MprcText>, value: "delete" });
        return group1;
    }, [viewData]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const RenderContent = (
        <MprcContainer>
            <MprcCard>
                <MprcCardFilterBox>
                    <MprcFilterLine>
                        <Grid container justifyContent="space-between" alignItems="center" width="100%">

                            <Grid>
                                <ButtonAdd size="large" color="inherit" onClick={handleAdd} text={t("ShortVideo.List.NewVideoButton")} />
                            </Grid>

                            <Grid>

                                {
                                    permissionExplorer.hasOne([CorePermissions.KnowledgeChannelsAdmin]) &&
                                    <MprcIconButton
                                        title={t("Channel.List.ListViewModeChannelAdminText")}
                                        color={viewFilter.filter.adminList ? "warning" : "inherit"}
                                        onClick={() => handleFilterChange({ adminList: !viewFilter.filter.adminList }, true)}
                                    >
                                        <MprcIconFa icon={faUsersViewfinder} size="sm" />
                                    </MprcIconButton>
                                }
                                <IconButtonBlocked
                                    color={viewFilter.filter.showExpired ? "warning" : undefined}
                                    onClick={() => handleFilterChange({ showExpired: !viewFilter.filter.showExpired }, true)}
                                    title={t("shared:Actions.ListExpired")}
                                />

                                <MprcIconButtonDropdown
                                    menuItemCheck
                                    icon={<IconSort />}
                                    selected={viewFilter.orderBy}
                                    onChange={handleOrderBy}
                                    items={[
                                        { text: t("ShortVideo.List.OrderBy.Title"), value: ShortVideoListOrderBy.Title },
                                        { text: t("ShortVideo.List.OrderBy.MoreViews"), value: ShortVideoListOrderBy.MoreViews },
                                        { text: t("ShortVideo.List.OrderBy.MoreLikes"), value: ShortVideoListOrderBy.MoreLikes },
                                        { text: t("ShortVideo.List.OrderBy.MoreComments"), value: ShortVideoListOrderBy.MoreComments },
                                        { text: t("ShortVideo.List.OrderBy.MoreRecent"), value: ShortVideoListOrderBy.MoreRecent },
                                        { text: t("ShortVideo.List.OrderBy.DueDate"), value: ShortVideoListOrderBy.DueDate },
                                    ]}
                                    title={t("Channel.List.OrderByButtonTooltip")}
                                />
                            </Grid>

                        </Grid>
                    </MprcFilterLine>

                    <MprcSearchTextField
                        autoFocus
                        placeholder={t("Search.TextPlaceholder", { ns: "shared" })}
                        confirmMode="debounce"
                        ref={searchTextField}
                        onConfirm={(text) => handleFilterChange({ searchText: text }, true)}
                    />
                </MprcCardFilterBox>

                <CardContent>
                    {
                        loading
                            ? <MprcSpinnerIndicator />
                            : viewData &&
                            <>
                                <MprcContainer>
                                    <MprcFilterCount
                                        loading={loading}
                                        isEmpty={viewData.totalRows == 0}
                                        countText={t("ShortVideo.List.RecordsFoundText", { count: viewData.totalRows })}
                                        filter={{
                                            active: filtered,
                                            text: t("ShortVideo.List.RecordsFilteredText", { count: viewData.totalRows }),
                                            onResetFilter: resetFilters
                                        }}
                                    />
                                </MprcContainer>

                                {containsConfidential &&
                                    <MprcContainer margin="normal">
                                        <MprcNotice
                                            elevation="small"
                                            size="small"
                                            items={
                                                [{
                                                    text: <Trans t={t} i18nKey="ShortVideo.List.ContainsConfidentialDisclaimer" />
                                                }]
                                            }
                                        />
                                    </MprcContainer>
                                }

                                {
                                    viewData && viewData.totalRows > 0 &&
                                    <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                        {
                                            viewData?.items.map((item, index) => {
                                                return (
                                                    <Grid key={index} width="100%">
                                                        <Grid container justifyContent="start" alignItems="flex-start" spacing={1}>
                                                            <Grid>
                                                                <IconButton onClick={() => handlePreview(item)} sx={{ p: 0 }}>
                                                                    <FontAwesomeIcon icon={faPlayCircle} fixedWidth size="xs" />
                                                                </IconButton>
                                                            </Grid>

                                                            <Grid>
                                                                <MprcListItemTitle onClick={() => handleEdit(item.shortVideoId, item.publicationId, item.title)}>
                                                                    {item.title}
                                                                </MprcListItemTitle>
                                                                <div>
                                                                    <Typography variant="caption">
                                                                        <FontAwesomeIcon icon={faClock} fixedWidth />
                                                                        {Formaters.playTimer(item.startTime)}&nbsp;&ndash;&nbsp;{Formaters.playTimer(item.endTime)}
                                                                    </Typography>

                                                                    {
                                                                        item.dueDate != null &&
                                                                        <Typography variant="caption">
                                                                            &nbsp;&nbsp;
                                                                            <FontAwesomeIcon icon={faCalendar} fixedWidth />
                                                                            {Formaters.localeDate(item.dueDate)}
                                                                        </Typography>
                                                                    }
                                                                </div>
                                                            </Grid>
                                                            <Grid>
                                                                <MprcIconButtonMoreOptionsDropdown
                                                                    paddingVariant="disabled"
                                                                    color="muted"
                                                                    onChange={(selection) => handleSelectUserOption(selection, item)}
                                                                    items={getUserMenuItems}
                                                                />
                                                            </Grid>

                                                        </Grid>
                                                    </Grid>
                                                );
                                            })
                                        }
                                    </Grid>
                                }
                            </>
                    }
                </CardContent>
                <MprcCardActions>
                    {viewData && viewData.totalPages > 1 && (
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != viewFilter.pageNumber) {
                                    setViewFilter({
                                        ...viewFilter,
                                        pageNumber
                                    });
                                    setLoading(true);
                                }
                            }}
                        />
                    )}
                </MprcCardActions>
            </MprcCard>
        </MprcContainer>
    );

    const RenderNotice = (
        <MprcContainer>
            <MprcCard paperColor="notice">
                <CardContent>
                    <MprcContainer>
                        <MprcCaretSwitch text={t("ShortVideo.List.NoticeTitle")} expanded={showNotice} onClick={() => setShowNotice(!showNotice)} />
                    </MprcContainer>
                    {
                        showNotice &&
                        <MprcCaptionBullets
                            text={
                                [
                                    t("ShortVideo.List.Notice_L1"),
                                    t("ShortVideo.List.Notice_L2"),
                                    t("ShortVideo.List.Notice_L3"),
                                    t("ShortVideo.List.Notice_L4"),
                                    t("ShortVideo.List.Notice_L5"),
                                    t("ShortVideo.List.Notice_L6"),
                                    t("ShortVideo.List.Notice_L7")
                                ]
                            }
                        />
                    }
                </CardContent>
            </MprcCard>
        </MprcContainer>
    );

    return (
        <>
            {dialog}

            <MprcRequestIndicator visible={deleteShortVideoId != null} type={"post"} />

            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : (
                        <MprcContainer>
                            <MprcContainer>
                                <MprcFormBarHeader
                                    actions={[
                                        <ButtonBack key="btnBack" rounded size="large" variant="text" onClick={() => { navigate(-1); }} />,
                                        <MprcFormSubHeader margin="none" title={t("ShortVideo.List.HeaderTitle")} />
                                    ]}
                                />
                            </MprcContainer>
                            {
                                noAccess
                                    ? <MprcFilterEmptyDataset message={t("ShortVideo.NoAccess")} />
                                    : (
                                        <>
                                            {RenderContent}
                                            {RenderNotice}
                                        </>
                                    )
                            }
                        </MprcContainer>
                    )
            }
        </>
    );
};
